import { numberToDecimalString as toDecimal } from 'commons/utils/number'
import {
  EmptyContent,
  type HeadItemType,
  Icon,
  Pagination,
  SendMessageTooltip,
  Table,
} from 'components'
import type { RankingItem } from 'services/home/interfaces'
import { getRanking, setRankingPage } from 'store/HomeContext/homeSlice'
import { useDispatch, useSelector } from 'store/store'

import type { OfficeTypes } from '../GoalProgress'
import { officeColors } from '../GoalProgress/constants'
import * as S from './styles'

interface RankingTableProps {
  quota: string
  selectedOffice: OfficeTypes
  selectedGroup: string | null
  screenshotComponentRef: React.MutableRefObject<null>
}

const RankingTable: React.FC<RankingTableProps> = ({
  quota,
  selectedOffice,
  selectedGroup,
  screenshotComponentRef,
}) => {
  const dispatch = useDispatch()
  const {
    loadingRanking: loading,
    rankingData,
    rankingPag: { totalPages, totalItems, currentPage, pageSize },
  } = useSelector((state) => state.home)

  const starColors = ['#D9B926', '#8E8E8E', '#821B17']

  const getZoneUser = (item: RankingItem) => {
    if (item.NomeUsuarioZona && item.EmailUsuarioZona) {
      return (
        <SendMessageTooltip
          email={item.EmailUsuarioZona.split(';')[0]}
          name={item.NomeUsuarioZona}
          phone={item.TelefoneUsuarioZona}
        >
          <span>{item.CodigoUsuarioZona ?? item.DescricaoZona}</span>
        </SendMessageTooltip>
      )
    }
    return <span>{item.DescricaoZona}</span>
  }

  const tableLabels = (): HeadItemType[] => {
    const tableHead: HeadItemType[] = [
      { columnName: 'Posicao', label: 'Posição' },
      { columnName: 'Zona', label: 'ID zona' },
      { columnName: 'ZonaVenda', label: 'Zona de venda' },
      { columnName: 'MetaTotal', label: 'Meta total' },
      { columnName: 'VolumeAprovado', label: 'Vol. aprovado' },
      { columnName: 'PercentagemAprovada', label: '% aprovada' },
    ]

    const showOffice = selectedOffice === 'TODOS'

    if (showOffice)
      tableHead.splice(1, 0, {
        columnName: 'Escritorio',
        label: 'Escritório',
      })
    if (!selectedGroup)
      tableHead.splice(showOffice ? 2 : 1, 0, {
        columnName: 'Descricao',
        label: 'Regional',
      })

    return tableHead
  }

  const tableData = () => {
    return rankingData.map((item) => {
      const rowContent = [
        <S.DataWrap key={`position-${item.Zona}`}>
          {item.Posicao <= 3 ? (
            <S.StarIcon sx={{ color: starColors[item.Posicao - 1] }} />
          ) : (
            <S.EmptyIcon />
          )}
          {item.Posicao}
        </S.DataWrap>,
        item.Zona,
        getZoneUser(item),
        <S.DataWrap key={`goal-${item.Zona}`}>
          <Icon color={officeColors[selectedOffice]} icon="sandal" size="16" />
          <span>{toDecimal(item.MetaTotal)}</span>
        </S.DataWrap>,
        <S.DataWrap achieved={item.PercentualMeta >= 100} key={`approved-${item.Zona}`}>
          <Icon color={officeColors[selectedOffice]} icon="sandal" size="16" />
          <span>{toDecimal(item.VolumeAprovado)}</span>
        </S.DataWrap>,
        <S.DataWrap achieved={item.PercentualMeta >= 100} key={`percent-${item.Zona}`}>
          <span>{Number(item.PercentualMeta.toFixed(2))} %</span>
        </S.DataWrap>,
      ]
      const showOffice = selectedOffice === 'TODOS'
      if (showOffice) rowContent.splice(1, 0, item.Escritorio)
      if (!selectedGroup) rowContent.splice(showOffice ? 2 : 1, 0, item.DescricaoRegional)
      return {
        content: rowContent,
        id: item.Zona,
      }
    })
  }

  const handlePageChange = (page: number) => {
    dispatch(setRankingPage(page))
    dispatch(
      getRanking(
        {
          AnoMesCota: quota,
          Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
          Gerente: null,
          Regional: selectedGroup,
        },
        { linhas: 10, paginas: page - 1 },
      ),
    )
  }

  return !loading && !rankingData.length ? (
    <EmptyContent
      description="Não foi possível encontrar dados para o período selecionado. Revise as opções selecionadas e tente novamente."
      title="Nenhum resultado encontrado"
    />
  ) : (
    <>
      <Table
        bodyData={tableData()}
        headData={tableLabels()}
        rowHeight={48}
        tableRef={screenshotComponentRef}
      />
      <Pagination
        currentPage={currentPage}
        disabled={loading}
        itemsPerPage={pageSize}
        numberOfItems={totalItems}
        numberOfPages={totalPages}
        setCurrentPage={handlePageChange}
      />
    </>
  )
}

export default RankingTable
